import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// Common automotive materials
const MATERIALS = [
  // Metals
  { name: 'Steel (Cold-rolled)', category: 'Metal', hts_code: '7208.51.00', cost_per_kg: 1.20 },
  { name: 'Steel (Hot-rolled)', category: 'Metal', hts_code: '7208.10.00', cost_per_kg: 1.10 },
  { name: 'Aluminum - Sheet', category: 'Metal', hts_code: '7606.12.00', cost_per_kg: 2.50 },
  { name: 'Aluminum - Cast', category: 'Metal', hts_code: '7616.99.00', cost_per_kg: 2.30 },
  { name: 'Stainless Steel', category: 'Metal', hts_code: '7219.11.00', cost_per_kg: 3.50 },
  { name: 'Zinc Alloy', category: 'Metal', hts_code: '7907.00.00', cost_per_kg: 2.80 },
  
  // Plastics
  { name: 'ABS Plastic', category: 'Plastic', hts_code: '3903.30.00', cost_per_kg: 2.20 },
  { name: 'Polypropylene (PP)', category: 'Plastic', hts_code: '3902.10.00', cost_per_kg: 1.50 },
  { name: 'Polyethylene (PE)', category: 'Plastic', hts_code: '3901.20.00', cost_per_kg: 1.40 },
  { name: 'Polycarbonate (PC)', category: 'Plastic', hts_code: '3907.40.00', cost_per_kg: 3.00 },
  { name: 'PVC', category: 'Plastic', hts_code: '3904.10.00', cost_per_kg: 1.30 },
  { name: 'Nylon (PA)', category: 'Plastic', hts_code: '3908.10.00', cost_per_kg: 4.50 },
  
  // Rubber
  { name: 'EPDM Rubber', category: 'Rubber', hts_code: '4002.70.00', cost_per_kg: 3.20 },
  { name: 'NBR Rubber', category: 'Rubber', hts_code: '4002.59.00', cost_per_kg: 4.00 },
  { name: 'Natural Rubber', category: 'Rubber', hts_code: '4001.21.00', cost_per_kg: 2.50 },
  { name: 'Silicone Rubber', category: 'Rubber', hts_code: '4002.99.00', cost_per_kg: 8.00 },
  
  // Glass
  { name: 'Tempered Glass', category: 'Glass', hts_code: '7007.19.00', cost_per_kg: 5.00 },
  { name: 'Laminated Glass', category: 'Glass', hts_code: '7007.29.00', cost_per_kg: 6.50 },
  
  // Composites
  { name: 'Carbon Fiber', category: 'Composite', hts_code: '6815.10.00', cost_per_kg: 25.00 },
  { name: 'Fiberglass', category: 'Composite', hts_code: '7019.39.00', cost_per_kg: 3.50 },
  
  // Electronics
  { name: 'PCB Assembly', category: 'Electronics', hts_code: '8534.00.00', cost_per_kg: 15.00 },
  { name: 'Copper Wire', category: 'Electronics', hts_code: '7408.11.00', cost_per_kg: 8.00 },
  
  // Textiles
  { name: 'Polyester Fabric', category: 'Textile', hts_code: '5407.51.00', cost_per_kg: 5.50 },
  { name: 'Leather', category: 'Textile', hts_code: '4104.11.00', cost_per_kg: 12.00 },
  
  // Other
  { name: 'Foam (Polyurethane)', category: 'Other', hts_code: '3909.50.00', cost_per_kg: 2.80 },
  { name: 'Adhesive', category: 'Other', hts_code: '3506.91.00', cost_per_kg: 4.50 },
];

export default function AddMaterialDialog({ open, onClose, onAdd, currentTotal = 0 }) {
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [percentage, setPercentage] = useState('');
  const [error, setError] = useState('');

  const remaining = 100 - currentTotal;

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setSelectedMaterial('');
      setPercentage('');
      setError('');
    }
  }, [open]);

  const handleAdd = () => {
    setError('');

    // Validation
    if (!selectedMaterial) {
      setError('Please select a material');
      return;
    }

    const percentValue = parseFloat(percentage);
    if (isNaN(percentValue) || percentValue <= 0) {
      setError('Please enter a valid percentage greater than 0');
      return;
    }

    if (percentValue > remaining) {
      setError(`Percentage cannot exceed remaining ${remaining.toFixed(1)}%`);
      return;
    }

    // Find material details
    const material = MATERIALS.find(m => m.name === selectedMaterial);
    if (!material) {
      setError('Material not found');
      return;
    }

    // Call onAdd with material data
    onAdd({
      material_name: material.name,
      category: material.category,
      percentage: percentValue,
      cost_per_kg: material.cost_per_kg,
      hts_code: material.hts_code,
    });
  };

  const handlePercentageChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPercentage(value);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Add Material</DialogTitle>
              <DialogDescription>
                Select a material and enter its percentage
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X size={16} />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Material Selection */}
          <div className="space-y-2">
            <Label htmlFor="material">Material *</Label>
            <Select value={selectedMaterial} onValueChange={setSelectedMaterial}>
              <SelectTrigger id="material">
                <SelectValue placeholder="Select a material..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {MATERIALS.map((material) => (
                  <SelectItem key={material.name} value={material.name}>
                    <div className="flex items-center justify-between w-full">
                      <span>{material.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({material.category})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Percentage Input */}
          <div className="space-y-2">
            <Label htmlFor="percentage">Percentage *</Label>
            <Input
              id="percentage"
              type="text"
              placeholder="e.g., 65.0"
              value={percentage}
              onChange={handlePercentageChange}
            />
            <p className="text-xs text-gray-500">
              Current total: {currentTotal.toFixed(1)}% | Remaining: {remaining.toFixed(1)}%
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Add Material
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
